/**
 * OS-chrome localization (menus, dialogs, settings, empty states).
 * Diegetic content — websites, mails, file contents, terminal output,
 * boot text, the lorebook — deliberately stays in-world English.
 *
 * Keys ARE the English strings; unknown keys pass through untouched, so
 * `t(item.name)` safely localizes system names ("Documents") while
 * leaving user files alone. `{var}` placeholders are substituted via the
 * second argument.
 */

export type OsLang = 'en' | 'fr' | 'es' | 'ja';

export const resolveLang = (code?: string): OsLang => {
  const c = (code || '').toLowerCase();
  if (c.startsWith('fr')) return 'fr';
  if (c.startsWith('es')) return 'es';
  if (c.startsWith('ja')) return 'ja';
  return 'en';
};

type Entry = { fr: string; es: string; ja: string };

const D: Record<string, Entry> = {
  /* ── system item names ── */
  Desktop: { fr: 'Bureau', es: 'Escritorio', ja: 'デスクトップ' },
  Documents: { fr: 'Documents', es: 'Documentos', ja: '書類' },
  Downloads: { fr: 'Téléchargements', es: 'Descargas', ja: 'ダウンロード' },
  'Trash Bin': { fr: 'Corbeille', es: 'Papelera', ja: 'ゴミ箱' },
  Taskbar: { fr: 'Barre des tâches', es: 'Barra de tareas', ja: 'タスクバー' },

  /* ── context menu / desktop ── */
  'New Folder': { fr: 'Nouveau dossier', es: 'Nueva carpeta', ja: '新規フォルダ' },
  'New Text Note': { fr: 'Nouvelle note texte', es: 'Nueva nota de texto', ja: '新規テキストノート' },
  'Clean Up Icons': { fr: 'Ranger les icônes', es: 'Organizar iconos', ja: 'アイコンを整列' },
  'Change Wallpaper': { fr: "Changer le fond d'écran", es: 'Cambiar fondo de pantalla', ja: '壁紙を変更' },
  About: { fr: 'À propos', es: 'Acerca de', ja: '情報' },
  Settings: { fr: 'Réglages', es: 'Configuración', ja: '設定' },
  Open: { fr: 'Ouvrir', es: 'Abrir', ja: '開く' },
  Rename: { fr: 'Renommer', es: 'Cambiar nombre', ja: '名前を変更' },
  'Toss in the trash': { fr: 'Jeter à la corbeille', es: 'Tirar a la papelera', ja: 'ゴミ箱に捨てる' },
  'Toss 1 item in the trash': { fr: 'Jeter 1 élément à la corbeille', es: 'Tirar 1 elemento a la papelera', ja: '1個の項目をゴミ箱へ' },
  'Toss {n} items in the trash': { fr: 'Jeter {n} éléments à la corbeille', es: 'Tirar {n} elementos a la papelera', ja: '{n}個の項目をゴミ箱へ' },
  'Empty Trash': { fr: 'Vider la corbeille', es: 'Vaciar papelera', ja: 'ゴミ箱を空にする' },
  Restore: { fr: 'Restaurer', es: 'Restaurar', ja: '元に戻す' },
  'Restore {n} items': { fr: 'Restaurer {n} éléments', es: 'Restaurar {n} elementos', ja: '{n}個の項目を元に戻す' },
  'Delete permanently': { fr: 'Supprimer définitivement', es: 'Eliminar permanentemente', ja: '完全に削除' },
  'Delete {n} items permanently': { fr: 'Supprimer définitivement {n} éléments', es: 'Eliminar {n} elementos permanentemente', ja: '{n}個の項目を完全に削除' },
  'Close window': { fr: 'Fermer la fenêtre', es: 'Cerrar ventana', ja: 'ウィンドウを閉じる' },
  'New Folder here': { fr: 'Nouveau dossier ici', es: 'Nueva carpeta aquí', ja: 'ここに新規フォルダ' },
  'New Text File here': { fr: 'Nouveau fichier texte ici', es: 'Nuevo archivo de texto aquí', ja: 'ここに新規テキスト' },
  Cancel: { fr: 'Annuler', es: 'Cancelar', ja: 'キャンセル' },
  Erase: { fr: 'Effacer', es: 'Borrar', ja: '消去' },
  '"{name}" will be erased forever. This cannot be undone.': {
    fr: '« {name} » sera effacé pour toujours. Cette action est irréversible.',
    es: '"{name}" se borrará para siempre. Esto no se puede deshacer.',
    ja: '「{name}」は完全に消去されます。この操作は取り消せません。',
  },
  '{n} items will be erased forever. This cannot be undone.': {
    fr: '{n} éléments seront effacés pour toujours. Cette action est irréversible.',
    es: '{n} elementos se borrarán para siempre. Esto no se puede deshacer.',
    ja: '{n}個の項目が完全に消去されます。この操作は取り消せません。',
  },
  'Everything in the trash will be erased forever. This cannot be undone.': {
    fr: 'Tout le contenu de la corbeille sera effacé pour toujours. Cette action est irréversible.',
    es: 'Todo el contenido de la papelera se borrará para siempre. Esto no se puede deshacer.',
    ja: 'ゴミ箱の中身はすべて完全に消去されます。この操作は取り消せません。',
  },

  /* ── file explorer ── */
  'This folder is empty.': { fr: 'Ce dossier est vide.', es: 'Esta carpeta está vacía.', ja: 'このフォルダは空です。' },
  'The trash is empty.': { fr: 'La corbeille est vide.', es: 'La papelera está vacía.', ja: 'ゴミ箱は空です。' },

  /* ── start menu ── */
  'Hey there early bird!': { fr: 'Salut, lève-tôt !', es: '¡Hola, madrugador!', ja: '早起きですね！' },
  'Good morning!': { fr: 'Bonjour !', es: '¡Buenos días!', ja: 'おはようございます！' },
  'Good afternoon!': { fr: 'Bon après-midi !', es: '¡Buenas tardes!', ja: 'こんにちは！' },
  'Good evening!': { fr: 'Bonsoir !', es: '¡Buenas noches!', ja: 'こんばんは！' },
  'Go to bed... -->': { fr: 'Va te coucher… -->', es: 'Vete a dormir… -->', ja: 'もう寝なさい… -->' },
  'Shut Down…': { fr: 'Éteindre…', es: 'Apagar…', ja: 'シャットダウン…' },

  /* ── preferences ── */
  Preferences: { fr: 'Préférences', es: 'Preferencias', ja: '環境設定' },
  Appearance: { fr: 'Apparence', es: 'Apariencia', ja: '外観' },
  Screensaver: { fr: 'Écran de veille', es: 'Salvapantallas', ja: 'スクリーンセーバー' },
  Network: { fr: 'Réseau', es: 'Red', ja: 'ネットワーク' },
  Security: { fr: 'Sécurité', es: 'Seguridad', ja: 'セキュリティ' },
  Identity: { fr: 'Identité', es: 'Identidad', ja: '識別情報' },
  Language: { fr: 'Langue', es: 'Idioma', ja: '言語' },
  'Computer Identity': { fr: "Identité de l'ordinateur", es: 'Identidad del equipo', ja: 'コンピュータ情報' },
  'Computer Name': { fr: "Nom de l'ordinateur", es: 'Nombre del equipo', ja: 'コンピュータ名' },
  Randomize: { fr: 'Aléatoire', es: 'Aleatorio', ja: 'ランダム' },
  'Shown in screensavers and network dialogs.': {
    fr: "Affiché dans les écrans de veille et les boîtes de dialogue réseau.",
    es: 'Se muestra en salvapantallas y diálogos de red.',
    ja: 'スクリーンセーバーやネットワーク画面に表示されます。',
  },
  Theme: { fr: 'Thème', es: 'Tema', ja: 'テーマ' },
  Classic: { fr: 'Classique', es: 'Clásico', ja: 'クラシック' },
  'Accent (Title Bar)': { fr: 'Accent (barre de titre)', es: 'Acento (barra de título)', ja: 'アクセント（タイトルバー）' },
  Wallpaper: { fr: "Fond d'écran", es: 'Fondo de pantalla', ja: '壁紙' },
  'Rotate Wallpaper': { fr: "Rotation du fond d'écran", es: 'Rotar fondo de pantalla', ja: '壁紙のローテーション' },
  Off: { fr: 'Désactivé', es: 'Desactivado', ja: 'オフ' },
  'Every 5 minutes': { fr: 'Toutes les 5 minutes', es: 'Cada 5 minutos', ja: '5分ごと' },
  'Every 15 minutes': { fr: 'Toutes les 15 minutes', es: 'Cada 15 minutos', ja: '15分ごと' },
  'Every 30 minutes': { fr: 'Toutes les 30 minutes', es: 'Cada 30 minutos', ja: '30分ごと' },
  'Every hour': { fr: 'Toutes les heures', es: 'Cada hora', ja: '1時間ごと' },
  'Every 4 hours': { fr: 'Toutes les 4 heures', es: 'Cada 4 horas', ja: '4時間ごと' },
  Enabled: { fr: 'Activé', es: 'Activado', ja: '有効' },
  Delay: { fr: 'Délai', es: 'Retraso', ja: '待ち時間' },
  Mode: { fr: 'Mode', es: 'Modo', ja: 'モード' },
  Firewall: { fr: 'Pare-feu', es: 'Cortafuegos', ja: 'ファイアウォール' },
  Internet: { fr: 'Internet', es: 'Internet', ja: 'インターネット' },
  'Enable Internet': { fr: 'Activer Internet', es: 'Activar Internet', ja: 'インターネットを有効化' },
  Status: { fr: 'État', es: 'Estado', ja: '状態' },
  'Not connected': { fr: 'Non connecté', es: 'No conectado', ja: '未接続' },
  'Available Networks': { fr: 'Réseaux disponibles', es: 'Redes disponibles', ja: '利用可能なネットワーク' },
  Wireless: { fr: 'Sans fil', es: 'Inalámbrico', ja: 'ワイヤレス' },
  Saved: { fr: 'Enregistré', es: 'Guardado', ja: '保存済み' },
  Connected: { fr: 'Connecté', es: 'Conectado', ja: '接続済み' },
  Connect: { fr: 'Connexion', es: 'Conectar', ja: '接続' },
  Disconnect: { fr: 'Déconnecter', es: 'Desconectar', ja: '切断' },
  'Saved Networks': { fr: 'Réseaux enregistrés', es: 'Redes guardadas', ja: '保存済みネットワーク' },
  Forget: { fr: 'Oublier', es: 'Olvidar', ja: '削除' },
  'Language & Region': { fr: 'Langue et région', es: 'Idioma y región', ja: '言語と地域' },
  'Enter Password': { fr: 'Saisir le mot de passe', es: 'Introducir contraseña', ja: 'パスワードを入力' },
  Password: { fr: 'Mot de passe', es: 'Contraseña', ja: 'パスワード' },
  'This password will be saved for this network.': {
    fr: 'Ce mot de passe sera enregistré pour ce réseau.',
    es: 'Esta contraseña se guardará para esta red.',
    ja: 'このパスワードはこのネットワーク用に保存されます。',
  },
  'Incorrect password. Please try again.': {
    fr: 'Mot de passe incorrect. Réessayez.',
    es: 'Contraseña incorrecta. Inténtalo de nuevo.',
    ja: 'パスワードが違います。もう一度お試しください。',
  },
  'This network requires a password.': {
    fr: 'Ce réseau nécessite un mot de passe.',
    es: 'Esta red requiere contraseña.',
    ja: 'このネットワークにはパスワードが必要です。',
  },

  /* ── shared dialogs ── */
  'Application Error': { fr: "Erreur d'application", es: 'Error de aplicación', ja: 'アプリケーションエラー' },
  Success: { fr: 'Succès', es: 'Éxito', ja: '成功' },
  'Press OK to try again.': { fr: 'Appuyez sur OK pour réessayer.', es: 'Pulsa OK para reintentar.', ja: 'OKを押して再試行してください。' },
  'Details…': { fr: 'Détails…', es: 'Detalles…', ja: '詳細…' },
  'Hide details': { fr: 'Masquer les détails', es: 'Ocultar detalles', ja: '詳細を隠す' },
  Close: { fr: 'Fermer', es: 'Cerrar', ja: '閉じる' },
  Continue: { fr: 'Continuer', es: 'Continuar', ja: '続行' },
  'This application has performed an illegal operation.': {
    fr: 'Cette application a effectué une opération illégale.',
    es: 'Esta aplicación ha realizado una operación ilegal.',
    ja: 'このアプリケーションは不正な処理を行いました。',
  },
  'Save As': { fr: 'Enregistrer sous', es: 'Guardar como', ja: '名前を付けて保存' },
  'Create Note': { fr: 'Créer une note', es: 'Crear nota', ja: 'ノートを作成' },
  Folders: { fr: 'Dossiers', es: 'Carpetas', ja: 'フォルダ' },
  'Existing Text Files': { fr: 'Fichiers texte existants', es: 'Archivos de texto existentes', ja: '既存のテキストファイル' },
  'File name:': { fr: 'Nom du fichier :', es: 'Nombre de archivo:', ja: 'ファイル名：' },
  Create: { fr: 'Créer', es: 'Crear', ja: '作成' },
  Computer: { fr: 'Ordinateur', es: 'Equipo', ja: 'コンピュータ' },
  Save: { fr: 'Enregistrer', es: 'Guardar', ja: '保存' },

  /* ── notepad ── */
  'File:': { fr: 'Fichier :', es: 'Archivo:', ja: 'ファイル：' },
  New: { fr: 'Nouveau', es: 'Nuevo', ja: '新規' },
  'Saving…': { fr: 'Enregistrement…', es: 'Guardando…', ja: '保存中…' },
  Unsaved: { fr: 'Non enregistré', es: 'Sin guardar', ja: '未保存' },
  '• Unsaved changes': { fr: '• Modifications non enregistrées', es: '• Cambios sin guardar', ja: '• 未保存の変更' },
  Ready: { fr: 'Prêt', es: 'Listo', ja: '準備完了' },

  /* ── mail ── */
  Inbox: { fr: 'Boîte de réception', es: 'Bandeja de entrada', ja: '受信トレイ' },
  Sent: { fr: 'Envoyés', es: 'Enviados', ja: '送信済み' },
  Spam: { fr: 'Indésirables', es: 'Spam', ja: '迷惑メール' },
  Trash: { fr: 'Corbeille', es: 'Papelera', ja: 'ゴミ箱' },
  Account: { fr: 'Compte', es: 'Cuenta', ja: 'アカウント' },
  From: { fr: 'De', es: 'De', ja: '差出人' },
  Subject: { fr: 'Objet', es: 'Asunto', ja: '件名' },
  Date: { fr: 'Date', es: 'Fecha', ja: '日付' },
  'No messages.': { fr: 'Aucun message.', es: 'No hay mensajes.', ja: 'メッセージはありません。' },
  'Select a message to preview.': { fr: 'Sélectionnez un message.', es: 'Selecciona un mensaje.', ja: 'メッセージを選択してください。' },
  Compose: { fr: 'Nouveau message', es: 'Redactar', ja: '作成' },
  To: { fr: 'À', es: 'Para', ja: '宛先' },
  Body: { fr: 'Message', es: 'Cuerpo', ja: '本文' },
  Send: { fr: 'Envoyer', es: 'Enviar', ja: '送信' },
  'Search mail': { fr: 'Rechercher…', es: 'Buscar correo', ja: 'メールを検索' },
  'Write your message…': { fr: 'Écrivez votre message…', es: 'Escribe tu mensaje…', ja: 'メッセージを入力…' },
  '(no subject)': { fr: '(sans objet)', es: '(sin asunto)', ja: '（件名なし）' },
  'There is no Internet connection': {
    fr: 'Aucune connexion Internet',
    es: 'No hay conexión a Internet',
    ja: 'インターネットに接続されていません',
  },

  /* ── preferences: network details ── */
  Disabled: { fr: 'Désactivé', es: 'Desactivado', ja: '無効' },
  'Online ({ssid})': { fr: 'En ligne ({ssid})', es: 'En línea ({ssid})', ja: 'オンライン（{ssid}）' },
  'Scanning…': { fr: 'Recherche…', es: 'Buscando…', ja: 'スキャン中…' },
  'Scan Again': { fr: 'Analyser à nouveau', es: 'Escanear de nuevo', ja: '再スキャン' },
  'Last scan: {time}': { fr: 'Dernière analyse : {time}', es: 'Último escaneo: {time}', ja: '前回のスキャン：{time}' },
  'Wireless is unavailable while Internet is disabled. Enable Internet to scan and connect.': {
    fr: "Le sans-fil est indisponible quand Internet est désactivé. Activez Internet pour analyser et vous connecter.",
    es: 'La red inalámbrica no está disponible con Internet desactivado. Activa Internet para escanear y conectar.',
    ja: 'インターネットが無効の間はワイヤレスを利用できません。有効にしてからスキャン・接続してください。',
  },
  Searching: { fr: 'Recherche', es: 'Buscando', ja: '検索中' },
  '(No networks found. Try scanning again or move closer to a relay.)': {
    fr: "(Aucun réseau trouvé. Réessayez ou rapprochez-vous d'un relais.)",
    es: '(No se encontraron redes. Escanea de nuevo o acércate a un repetidor.)',
    ja: '（ネットワークが見つかりません。再スキャンするか、中継器に近づいてください。）',
  },
  '(Nothing saved yet)': { fr: "(Rien d'enregistré pour le moment)", es: '(Nada guardado todavía)', ja: '（まだ何も保存されていません）' },
  'Saved {date}': { fr: 'Enregistré le {date}', es: 'Guardado el {date}', ja: '{date} に保存' },
  'Turning Internet off preserves your saved networks. Re-enabling will auto-join the last connection if available.': {
    fr: 'Désactiver Internet conserve vos réseaux enregistrés. À la réactivation, la dernière connexion sera rejointe automatiquement si possible.',
    es: 'Desactivar Internet conserva tus redes guardadas. Al reactivarlo se reconectará automáticamente a la última red disponible.',
    ja: 'インターネットを無効にしても保存済みネットワークは保持されます。再度有効にすると、前回の接続に自動的に再接続します。',
  },

  /* ── task manager / app hub ── */
  Processes: { fr: 'Processus', es: 'Procesos', ja: 'プロセス' },
  Performance: { fr: 'Performances', es: 'Rendimiento', ja: 'パフォーマンス' },
  'Find:': { fr: 'Rechercher :', es: 'Buscar:', ja: '検索：' },
  Priority: { fr: 'Priorité', es: 'Prioridad', ja: '優先度' },
  Actions: { fr: 'Actions', es: 'Acciones', ja: '操作' },
  Low: { fr: 'Basse', es: 'Baja', ja: '低' },
  Normal: { fr: 'Normale', es: 'Normal', ja: '中' },
  High: { fr: 'Haute', es: 'Alta', ja: '高' },
  'End Task': { fr: 'Fin de tâche', es: 'Finalizar tarea', ja: 'タスクの終了' },
  Uptime: { fr: 'Durée', es: 'Tiempo activo', ja: '稼働時間' },
  'Show system': { fr: 'Afficher système', es: 'Mostrar sistema', ja: 'システムを表示' },
  Focus: { fr: 'Focus', es: 'Foco', ja: '前面へ' },
  End: { fr: 'Fin', es: 'Fin', ja: '終了' },
  'End All': { fr: 'Tout fermer', es: 'Finalizar todo', ja: 'すべて終了' },
  'No processes match your filters.': {
    fr: 'Aucun processus ne correspond aux filtres.',
    es: 'Ningún proceso coincide con los filtros.',
    ja: 'フィルタに一致するプロセスはありません。',
  },
  'Installation in progress...': { fr: 'Installation en cours…', es: 'Instalación en curso…', ja: 'インストール中…' },
  'Uninstalling...': { fr: 'Désinstallation…', es: 'Desinstalando…', ja: 'アンインストール中…' },
  Memory: { fr: 'Mémoire', es: 'Memoria', ja: 'メモリ' },
  Name: { fr: 'Nom', es: 'Nombre', ja: '名前' },
  State: { fr: 'État', es: 'Estado', ja: '状態' },
  'Search for your favorite app!': {
    fr: 'Cherchez votre appli préférée !',
    es: '¡Busca tu app favorita!',
    ja: 'お気に入りのアプリを探そう！',
  },
  Install: { fr: 'Installer', es: 'Instalar', ja: 'インストール' },
  Uninstall: { fr: 'Désinstaller', es: 'Desinstalar', ja: 'アンインストール' },
  /* ── app names ── */
  Mail: { fr: 'Courrier', es: 'Correo', ja: 'メール' },
  'File Explorer': { fr: 'Explorateur de fichiers', es: 'Explorador de archivos', ja: 'ファイルエクスプローラ' },
  'Task Manager': { fr: 'Gestionnaire de tâches', es: 'Administrador de tareas', ja: 'タスクマネージャ' },
  'To-do': { fr: 'À faire', es: 'Tareas', ja: 'やること' },
  'File Transfer': { fr: 'Transfert de fichiers', es: 'Transferencia de archivos', ja: 'ファイル転送' },
  'Laurel CV': { fr: 'CV de Laurel', es: 'CV de Laurel', ja: 'ローレルの履歴書' },
  Terminal: { fr: 'Terminal', es: 'Terminal', ja: 'ターミナル' },
  Notepad: { fr: 'Bloc-notes', es: 'Bloc de notas', ja: 'メモ帳' },
  Canvas: { fr: 'Canvas', es: 'Lienzo', ja: 'キャンバス' },
  Calculator: { fr: 'Calculatrice', es: 'Calculadora', ja: '電卓' },
  Chess: { fr: 'Échecs', es: 'Ajedrez', ja: 'チェス' },
  'Video Poker': { fr: 'Vidéo Poker', es: 'Videopóker', ja: 'ビデオポーカー' },
  Mahjong: { fr: 'Mah-jong', es: 'Mahjong', ja: '麻雀' },
  Radio: { fr: 'Radio', es: 'Radio', ja: 'ラジオ' },
  'App Hub': { fr: 'App Hub', es: 'App Hub', ja: 'アプリハブ' },
  'Start Menu': { fr: 'Menu Démarrer', es: 'Menú Inicio', ja: 'スタートメニュー' },
  'Context Menu': { fr: 'Menu contextuel', es: 'Menú contextual', ja: 'コンテキストメニュー' },

  /* ── findex homepage ── */
  'One Query Away.': { fr: 'À une requête près.', es: 'A una consulta de distancia.', ja: '一つのクエリで、すべてへ。' },
  'What are you looking for?': { fr: 'Que cherchez-vous ?', es: '¿Qué estás buscando?', ja: '何をお探しですか？' },
  'Feeling Lucky': { fr: "J'ai de la chance", es: 'Voy a tener suerte', ja: 'ラッキーな気分' },
  Query: { fr: 'Rechercher', es: 'Consultar', ja: '検索' },
  'index status: fragmented · ~72% of the pre-Wipe web is WITHHELD or STERILIZED · queries are identity-logged on CivNet': {
    fr: "état de l'index : fragmenté · ~72 % du web pré-Wipe est RETENU ou STÉRILISÉ · les requêtes sont journalisées sur CivNet",
    es: 'estado del índice: fragmentado · ~72% de la web pre-Wipe está RETENIDA o ESTERILIZADA · las consultas se registran en CivNet',
    ja: 'インデックス状態：断片化 · Wipe以前のウェブの約72%は非公開または消毒済み · クエリはCivNet上で記録されます',
  },

  /* ── mail actions ── */
  'Mark Read': { fr: 'Marquer lu', es: 'Marcar leído', ja: '既読にする' },
  'Mark Unread': { fr: 'Marquer non lu', es: 'Marcar no leído', ja: '未読にする' },
  Delete: { fr: 'Supprimer', es: 'Eliminar', ja: '削除' },
  Reply: { fr: 'Répondre', es: 'Responder', ja: '返信' },
  'Mark spam': { fr: 'Marquer indésirable', es: 'Marcar spam', ja: '迷惑メール報告' },

  /* ── to-do ── */
  Add: { fr: 'Ajouter', es: 'Añadir', ja: '追加' },
  'Sort:': { fr: 'Tri :', es: 'Orden:', ja: '並び替え：' },
  Manual: { fr: 'Manuel', es: 'Manual', ja: '手動' },
  Due: { fr: 'Échéance', es: 'Vencimiento', ja: '期限' },
  Smart: { fr: 'Intelligent', es: 'Inteligente', ja: 'スマート' },
  Lists: { fr: 'Listes', es: 'Listas', ja: 'リスト' },
  Tags: { fr: 'Étiquettes', es: 'Etiquetas', ja: 'タグ' },
  'New list…': { fr: 'Nouvelle liste…', es: 'Nueva lista…', ja: '新規リスト…' },
  'Exit Focus': { fr: 'Quitter le focus', es: 'Salir del enfoque', ja: 'フォーカス解除' },
  'Due:': { fr: 'Échéance :', es: 'Vence:', ja: '期限：' },
  Today: { fr: "Aujourd'hui", es: 'Hoy', ja: '今日' },
  Tomorrow: { fr: 'Demain', es: 'Mañana', ja: '明日' },
  'Next Week': { fr: 'Semaine prochaine', es: 'Próxima semana', ja: '来週' },
  'Pick…': { fr: 'Choisir…', es: 'Elegir…', ja: '選択…' },
  'Priority:': { fr: 'Priorité :', es: 'Prioridad:', ja: '優先度：' },
  'Waiting:': { fr: 'En attente :', es: 'Esperando:', ja: '待機：' },
  Start: { fr: 'Démarrer', es: 'Iniciar', ja: '開始' },
  Pause: { fr: 'Pause', es: 'Pausa', ja: '一時停止' },
  Stop: { fr: 'Arrêter', es: 'Detener', ja: '停止' },
  'Type to add…  (#tag @today ^daily ~30m !!!)': {
    fr: 'Tapez pour ajouter…  (#tag @today ^daily ~30m !!!)',
    es: 'Escribe para añadir…  (#tag @today ^daily ~30m !!!)',
    ja: '入力して追加…  (#tag @today ^daily ~30m !!!)',
  },
  'Search tasks, jump to list (#tag)…': {
    fr: 'Rechercher des tâches, aller à une liste (#tag)…',
    es: 'Buscar tareas, ir a una lista (#tag)…',
    ja: 'タスクを検索、リストへ移動 (#tag)…',
  },

  /* ── canvas ── */
  Tools: { fr: 'Outils', es: 'Herramientas', ja: 'ツール' },
  Brush: { fr: 'Pinceau', es: 'Pincel', ja: 'ブラシ' },
  'Size:': { fr: 'Taille :', es: 'Tamaño:', ja: 'サイズ：' },
  'Color:': { fr: 'Couleur :', es: 'Color:', ja: '色：' },

  /* ── chess ── */
  'New Game': { fr: 'Nouvelle partie', es: 'Nueva partida', ja: '新しいゲーム' },
  Forfeit: { fr: 'Abandonner', es: 'Rendirse', ja: '投了' },
  'Forfeit?': { fr: 'Abandonner ?', es: '¿Rendirse?', ja: '投了しますか？' },
  'Keep playing': { fr: 'Continuer la partie', es: 'Seguir jugando', ja: '続ける' },
  'Pick an opponent': { fr: 'Choisissez un adversaire', es: 'Elige un oponente', ja: '対戦相手を選択' },
  'Your move': { fr: 'À vous de jouer', es: 'Tu turno', ja: 'あなたの番' },
  'Black to move': { fr: 'Aux noirs de jouer', es: 'Mueven negras', ja: '黒の番' },
  'Check — your move!': { fr: 'Échec — à vous !', es: '¡Jaque — te toca!', ja: 'チェック — あなたの番！' },
  'Check on black': { fr: 'Échec au noir', es: 'Jaque al negro', ja: '黒にチェック' },
  'SliceOS is thinking…': { fr: 'SliceOS réfléchit…', es: 'SliceOS está pensando…', ja: 'SliceOSが考え中…' },
  Draw: { fr: 'Nulle', es: 'Tablas', ja: '引き分け' },
  'You win!': { fr: 'Vous gagnez !', es: '¡Ganas!', ja: 'あなたの勝ち！' },
  'SliceOS wins': { fr: 'SliceOS gagne', es: 'SliceOS gana', ja: 'SliceOSの勝ち' },
  'SliceOS Chess — New Game': { fr: 'SliceOS Échecs — Nouvelle partie', es: 'SliceOS Ajedrez — Nueva partida', ja: 'SliceOSチェス — 新しいゲーム' },
  'You play white. Choose your opponent:': { fr: 'Vous jouez les blancs. Choisissez votre adversaire :', es: 'Juegas con blancas. Elige tu oponente:', ja: 'あなたは白番です。対戦相手を選んでください：' },
  'Tip the king and concede this game to SliceOS?': {
    fr: 'Coucher le roi et concéder la partie à SliceOS ?',
    es: '¿Tumbar el rey y conceder la partida a SliceOS?',
    ja: 'キングを倒してSliceOSに投了しますか？',
  },
  'Move {n}': { fr: 'Coup {n}', es: 'Jugada {n}', ja: '{n}手目' },
  'Stalemate — nobody can move, nobody wins.': { fr: 'Pat — personne ne peut jouer, personne ne gagne.', es: 'Ahogado — nadie puede mover, nadie gana.', ja: 'ステイルメイト — 誰も動けず、誰も勝てません。' },
  'You tipped the king. There is no shame in retreat. (Some, maybe.)': {
    fr: "Vous avez couché le roi. Il n'y a pas de honte à battre en retraite. (Un peu, peut-être.)",
    es: 'Tumbaste el rey. No hay vergüenza en retirarse. (Bueno, un poco.)',
    ja: 'キングを倒しました。撤退は恥ではありません。（少しは恥かも。）',
  },

  /* ── mahjong ── */
  'Congratulations!': { fr: 'Félicitations !', es: '¡Felicidades!', ja: 'おめでとうございます！' },
  'No moves left': { fr: 'Plus aucun coup', es: 'Sin movimientos', ja: '手詰まり' },
  'You cleared the whole turtle. The tiles bow to you.': {
    fr: 'Vous avez vidé toute la tortue. Les tuiles vous saluent.',
    es: 'Despejaste toda la tortuga. Las fichas se inclinan ante ti.',
    ja: '亀を全て崩しました。牌があなたに頭を下げています。',
  },
  'No matching free pair remains. Shuffle the rest, undo, or start over.': {
    fr: 'Plus aucune paire libre. Mélangez le reste, annulez, ou recommencez.',
    es: 'No queda ningún par libre. Baraja el resto, deshaz o empieza de nuevo.',
    ja: '取れるペアがありません。残りをシャッフルするか、戻すか、やり直してください。',
  },
  Shuffle: { fr: 'Mélanger', es: 'Barajar', ja: 'シャッフル' },
  Undo: { fr: 'Annuler', es: 'Deshacer', ja: '戻す' },
  Hint: { fr: 'Indice', es: 'Pista', ja: 'ヒント' },

  /* ── video poker / slots / boot tower ── */
  BET: { fr: 'MISE', es: 'APUESTA', ja: 'ベット' },
  HOLD: { fr: 'GARDER', es: 'RETENER', ja: 'ホールド' },
  DEAL: { fr: 'DONNE', es: 'REPARTIR', ja: 'ディール' },
  DRAW: { fr: 'TIRAGE', es: 'CAMBIAR', ja: 'ドロー' },
  'Press DEAL to start': { fr: 'Appuyez sur DONNE pour commencer', es: 'Pulsa REPARTIR para empezar', ja: 'ディールを押してスタート' },
  'Press DEAL to start a new hand': { fr: 'Appuyez sur DONNE pour une nouvelle main', es: 'Pulsa REPARTIR para una nueva mano', ja: 'ディールを押して新しいハンドへ' },
  'How to Play — Jacks or Better': { fr: 'Comment jouer — Valets ou mieux', es: 'Cómo jugar — Jotas o mejor', ja: '遊び方 — ジャックスオアベター' },
  Rules: { fr: 'Règles', es: 'Reglas', ja: 'ルール' },
  Hand: { fr: 'Main', es: 'Mano', ja: 'ハンド' },
  Credits: { fr: 'Crédits', es: 'Créditos', ja: 'クレジット' },
  Lines: { fr: 'Lignes', es: 'Líneas', ja: 'ライン' },
  'Bet / Line': { fr: 'Mise / ligne', es: 'Apuesta / línea', ja: 'ベット／ライン' },
  'Total Bet': { fr: 'Mise totale', es: 'Apuesta total', ja: '総ベット' },
  Symbol: { fr: 'Symbole', es: 'Símbolo', ja: 'シンボル' },
  RUN: { fr: 'COURSE', es: 'CARRERA', ja: 'ラン' },
  FLOOR: { fr: 'ÉTAGE', es: 'PISO', ja: 'フロア' },
  Restart: { fr: 'Recommencer', es: 'Reiniciar', ja: 'リスタート' },
  'YOU LOST': { fr: 'PERDU', es: 'HAS PERDIDO', ja: 'ゲームオーバー' },

  /* ── terminals ── */
  'Type "help" for a list of commands.': { fr: 'Tapez « help » pour la liste des commandes.', es: 'Escribe "help" para ver los comandos.', ja: '「help」と入力するとコマンド一覧が表示されます。' },
  'Available commands:': { fr: 'Commandes disponibles :', es: 'Comandos disponibles:', ja: '利用可能なコマンド：' },
  'exit - Close the terminal app': { fr: "exit - Fermer le terminal", es: 'exit - Cerrar la terminal', ja: 'exit - ターミナルを閉じる' },
  'specs - List system specs': { fr: 'specs - Caractéristiques du système', es: 'specs - Especificaciones del sistema', ja: 'specs - システム仕様を表示' },
  'beep - Play a lovely beep sound': { fr: 'beep - Jouer un joli bip', es: 'beep - Reproducir un bonito bip', ja: 'beep - ビープ音を鳴らす' },
  'clear - Clear the terminal screen': { fr: "clear - Effacer l'écran", es: 'clear - Limpiar la pantalla', ja: 'clear - 画面をクリア' },
  'launch [app] - Launch an application': { fr: 'launch [app] - Lancer une application', es: 'launch [app] - Iniciar una aplicación', ja: 'launch [app] - アプリを起動' },
  'open [file] - Open a file or folder': { fr: 'open [file] - Ouvrir un fichier ou dossier', es: 'open [file] - Abrir un archivo o carpeta', ja: 'open [file] - ファイル／フォルダを開く' },
  'ls - List files in the current directory': { fr: 'ls - Lister les fichiers du dossier courant', es: 'ls - Listar archivos del directorio actual', ja: 'ls - 現在のディレクトリを一覧表示' },
  'cd [directory] - Change directory': { fr: 'cd [directory] - Changer de dossier', es: 'cd [directory] - Cambiar de directorio', ja: 'cd [directory] - ディレクトリを移動' },
  'No matching files found for "{q}"': { fr: 'Aucun fichier trouvé pour « {q} »', es: 'No se encontraron archivos para "{q}"', ja: '「{q}」に一致するファイルはありません' },
  'No matching apps found for "{q}"': { fr: 'Aucune application trouvée pour « {q} »', es: 'No se encontraron aplicaciones para "{q}"', ja: '「{q}」に一致するアプリはありません' },

  'This Week': { fr: 'Cette semaine', es: 'Esta semana', ja: '今週' },
  Waiting: { fr: 'En attente', es: 'En espera', ja: '待機' },
  Done: { fr: 'Terminé', es: 'Hecho', ja: '完了' },
  '{cmd}: command not found': { fr: '{cmd} : commande introuvable', es: '{cmd}: comando no encontrado', ja: '{cmd}：コマンドが見つかりません' },
  'launch: app not found: {q}': { fr: 'launch : application introuvable : {q}', es: 'launch: aplicación no encontrada: {q}', ja: 'launch：アプリが見つかりません：{q}' },

  /* ── laurel.sh résumé shell chrome ── */
  'Welcome. This is an interactive résumé — a little shell standing in for a portfolio.': {
    fr: 'Bienvenue. Ceci est un CV interactif — un petit shell en guise de portfolio.',
    es: 'Bienvenido. Este es un currículum interactivo: una pequeña shell haciendo de portafolio.',
    ja: 'ようこそ。これはインタラクティブな履歴書 — ポートフォリオ代わりの小さなシェルです。',
  },
  "Type help to see what's available, or cv to download my resume.": {
    fr: 'Tapez « help » pour voir les commandes, ou « cv » pour télécharger mon CV.',
    es: 'Escribe "help" para ver los comandos o "cv" para descargar mi currículum.',
    ja: '「help」でコマンド一覧、「cv」で履歴書をダウンロードできます。',
  },
  '// the résumé': { fr: '// le CV', es: '// el currículum', ja: '// 履歴書' },
  'Type a command and press enter. Use tab to autocomplete, ↑/↓ for history.': {
    fr: "Tapez une commande puis entrée. Tab pour compléter, ↑/↓ pour l'historique.",
    es: 'Escribe un comando y pulsa intro. Tab para autocompletar, ↑/↓ para el historial.',
    ja: 'コマンドを入力してEnter。Tabで補完、↑/↓で履歴。',
  },
  '── and to mess around ──────────────────────': {
    fr: "── et pour s'amuser ──────────────────────",
    es: '── y para jugar ──────────────────────',
    ja: '── お遊びコマンド ──────────────────────',
  },
  '…and a few hidden ones. 🥚': { fr: '…et quelques commandes cachées. 🥚', es: '…y algunos ocultos. 🥚', ja: '…隠しコマンドもあります。🥚' },
  'list available commands': { fr: 'liste des commandes', es: 'lista de comandos', ja: 'コマンド一覧' },
  'who I am': { fr: 'qui je suis', es: 'quién soy', ja: '私について' },
  "where I've worked": { fr: "où j'ai travaillé", es: 'dónde he trabajado', ja: '職歴' },
  "things I've built": { fr: "ce que j'ai construit", es: 'cosas que he creado', ja: '制作物' },
  'what I work with': { fr: 'mes outils', es: 'con qué trabajo', ja: '使用技術' },
  'where I studied': { fr: "où j'ai étudié", es: 'dónde estudié', ja: '学歴' },
  'how to reach me': { fr: 'me contacter', es: 'cómo contactarme', ja: '連絡先' },
  'download my CV (PDF)': { fr: 'télécharger mon CV (PDF)', es: 'descargar mi CV (PDF)', ja: '履歴書をダウンロード（PDF）' },
  'poke around the filesystem': { fr: 'fouiller le système de fichiers', es: 'husmear el sistema de archivos', ja: 'ファイルシステムを覗く' },
  'wander into a directory': { fr: 'entrer dans un dossier', es: 'entrar en un directorio', ja: 'ディレクトリへ移動' },
  'where am I again?': { fr: 'où suis-je déjà ?', es: '¿dónde estaba?', ja: '現在地は？' },
  'read a file out loud': { fr: 'lire un fichier à voix haute', es: 'leer un archivo en voz alta', ja: 'ファイルを読み上げる' },
  'wipe the slate clean': { fr: 'faire table rase', es: 'borrón y cuenta nueva', ja: '画面をまっさらに' },
  'repaint the phosphor': { fr: 'repeindre le phosphore', es: 'repintar el fósforo', ja: '画面を塗り替える' },
  "everything you've typed": { fr: 'tout ce que vous avez tapé', es: 'todo lo que has escrito', ja: '入力履歴' },
  'launch a link in a new tab': { fr: 'ouvrir un lien dans un nouvel onglet', es: 'abrir un enlace en otra pestaña', ja: 'リンクを新しいタブで開く' },
  'you tell me': { fr: 'à vous de me dire', es: 'dímelo tú', ja: 'あなた次第' },
  "I'll repeat after you": { fr: 'je répète après vous', es: 'repito lo que digas', ja: 'あなたの言葉を繰り返します' },
  'the manual (yes, RTFM)': { fr: 'le manuel (oui, RTFM)', es: 'el manual (sí, RTFM)', ja: 'マニュアル（RTFM）' },
  'end the session. (the résumé is not the whole machine.)': {
    fr: "terminer la session. (le CV n'est pas toute la machine.)",
    es: 'terminar la sesión. (el currículum no es toda la máquina.)',
    ja: 'セッションを終了。（履歴書がこのマシンの全てではない。）',
  },
  'keyboard clicks on/off': { fr: 'clics clavier on/off', es: 'clics de teclado sí/no', ja: 'キー音のオン／オフ' },

  /* ── canvas tools ── */
  Eraser: { fr: 'Gomme', es: 'Borrador', ja: '消しゴム' },
  Line: { fr: 'Ligne', es: 'Línea', ja: '直線' },
  Rect: { fr: 'Rectangle', es: 'Rectángulo', ja: '矩形' },
  Ellipse: { fr: 'Ellipse', es: 'Elipse', ja: '楕円' },
  Picker: { fr: 'Pipette', es: 'Cuentagotas', ja: 'スポイト' },
  Invert: { fr: 'Inverser', es: 'Invertir', ja: '反転' },
  Clear: { fr: 'Effacer', es: 'Limpiar', ja: '全消去' },
  Redo: { fr: 'Rétablir', es: 'Rehacer', ja: 'やり直す' },

  /* ── boot sequence ── */
  'Post-Wipe Verified System – Certified': {
    fr: 'Système vérifié post-Wipe – Certifié',
    es: 'Sistema verificado post-Wipe – Certificado',
    ja: 'Wipe後検証済みシステム – 認証済み',
  },
  'Initiating secure boot…': { fr: 'Démarrage sécurisé en cours…', es: 'Iniciando arranque seguro…', ja: 'セキュアブートを開始…' },
  'Initiating secure boot protocol...': {
    fr: 'Protocole de démarrage sécurisé en cours...',
    es: 'Iniciando protocolo de arranque seguro...',
    ja: 'セキュアブートプロトコルを開始...',
  },
  ' (Hold Q for Operator Setup)': {
    fr: ' (Maintenez Q pour la configuration opérateur)',
    es: ' (Mantén Q para configuración del operador)',
    ja: '（Qを長押しでオペレーター設定）',
  },
  'Firmware: Verified  ✔': { fr: 'Micrologiciel : vérifié  ✔', es: 'Firmware: verificado  ✔', ja: 'ファームウェア：検証済み  ✔' },
  'Kernel: Loaded  ✔': { fr: 'Noyau : chargé  ✔', es: 'Núcleo: cargado  ✔', ja: 'カーネル：読み込み完了  ✔' },
  'Filesystem: Clean  ✔': { fr: 'Système de fichiers : sain  ✔', es: 'Sistema de archivos: limpio  ✔', ja: 'ファイルシステム：正常  ✔' },
  'Network: Link up  ✔': { fr: 'Réseau : liaison établie  ✔', es: 'Red: enlace activo  ✔', ja: 'ネットワーク：リンク確立  ✔' },
  'Firewall: Compliance-Locked  ✔': {
    fr: 'Pare-feu : verrouillage conformité  ✔',
    es: 'Cortafuegos: bloqueo de conformidad  ✔',
    ja: 'ファイアウォール：コンプライアンス固定  ✔',
  },
  'Firewall: Adaptive  ✔': { fr: 'Pare-feu : adaptatif  ✔', es: 'Cortafuegos: adaptativo  ✔', ja: 'ファイアウォール：適応型  ✔' },
  'Services: Slice Service Daemon… OK  |  UI (Quartz Layer)… OK': {
    fr: 'Services : démon Slice… OK  |  UI (couche Quartz)… OK',
    es: 'Servicios: demonio Slice… OK  |  UI (capa Quartz)… OK',
    ja: 'サービス：Sliceデーモン… OK  |  UI（Quartzレイヤー）… OK',
  },
  'User space: /home/operator': { fr: 'Espace utilisateur : /home/operator', es: 'Espacio de usuario: /home/operator', ja: 'ユーザー空間：/home/operator' },
  'Welcome, Operator.': { fr: 'Bienvenue, opérateur.', es: 'Bienvenido, operador.', ja: 'ようこそ、オペレーター。' },
  'SliceOS Desktop is now active.': { fr: 'Le bureau SliceOS est actif.', es: 'El escritorio SliceOS está activo.', ja: 'SliceOSデスクトップが起動しました。' },
  'SliceOS Desktop Environment is now active.': {
    fr: "L'environnement de bureau SliceOS est actif.",
    es: 'El entorno de escritorio SliceOS está activo.',
    ja: 'SliceOSデスクトップ環境が起動しました。',
  },
  'Tip: Press F9 to access Compliance Mode.': {
    fr: 'Astuce : appuyez sur F9 pour le mode conformité.',
    es: 'Consejo: pulsa F9 para el modo de conformidad.',
    ja: 'ヒント：F9でコンプライアンスモード。',
  },
  'Tip: Press F9 to toggle Compliance Mode; F10 opens Network Diagnostics.': {
    fr: 'Astuce : F9 bascule le mode conformité ; F10 ouvre le diagnostic réseau.',
    es: 'Consejo: F9 alterna el modo de conformidad; F10 abre el diagnóstico de red.',
    ja: 'ヒント：F9でコンプライアンスモード切替、F10でネットワーク診断。',
  },
  'Checking firmware authenticity... [VERIFIED]': {
    fr: "Vérification de l'authenticité du micrologiciel... [VÉRIFIÉ]",
    es: 'Comprobando autenticidad del firmware... [VERIFICADO]',
    ja: 'ファームウェアの真正性を確認... [検証済み]',
  },
  'Launching UEFI Runtime... [OK]': { fr: 'Lancement du runtime UEFI... [OK]', es: 'Iniciando runtime UEFI... [OK]', ja: 'UEFIランタイムを起動... [OK]' },
  'Input Devices: Tactile Keyboard [OK]  |  PointSurface Interface [OK]': {
    fr: 'Périphériques : clavier tactile [OK]  |  interface PointSurface [OK]',
    es: 'Dispositivos: teclado táctil [OK]  |  interfaz PointSurface [OK]',
    ja: '入力デバイス：タクタイルキーボード [OK]  |  PointSurfaceインターフェース [OK]',
  },

  '// about': { fr: '// à propos', es: '// acerca de', ja: '// 自己紹介' },
  '// experience': { fr: '// expérience', es: '// experiencia', ja: '// 職歴' },
  '// projects': { fr: '// projets', es: '// proyectos', ja: '// プロジェクト' },
  '// skills': { fr: '// compétences', es: '// habilidades', ja: '// スキル' },
  '// education': { fr: '// formation', es: '// educación', ja: '// 学歴' },
  '// contact': { fr: '// contact', es: '// contacto', ja: '// 連絡先' },

  Drive: { fr: 'Disque', es: 'Unidad', ja: 'ドライブ' },
  'loading modules … about experience projects skills … ok': {
    fr: 'chargement des modules … about experience projects skills … ok',
    es: 'cargando módulos … about experience projects skills … ok',
    ja: 'モジュールを読み込み中 … about experience projects skills … ok',
  },
  'mounting /home/laurel … ok': { fr: 'montage de /home/laurel … ok', es: 'montando /home/laurel … ok', ja: '/home/laurel をマウント … ok' },
  'starting shell … ok': { fr: 'démarrage du shell … ok', es: 'iniciando shell … ok', ja: 'シェルを起動 … ok' },

};

export const translate = (lang: OsLang, key: string, vars?: Record<string, string | number>): string => {
  let out = lang === 'en' ? key : (D[key]?.[lang] ?? key);
  if (vars) {
    for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
  }
  return out;
};

